import { Controller } from "@hotwired/stimulus"
import { Chart, RadarController, BarController, LineController, DoughnutController, CategoryScale, LinearScale, BarElement, LineElement, PointElement, RadialLinearScale, ArcElement, Legend, Tooltip } from 'chart.js';

// Register the required Chart.js components
Chart.register(
  RadarController,
  BarController, 
  LineController,
  DoughnutController,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  RadialLinearScale,
  ArcElement,
  Legend,
  Tooltip
);

export default class extends Controller {
  static targets = ["radar", "bars", "distribution", "compatibility", "balance"]
  static values = { 
    dominance: Number,
    influence: Number,
    steadiness: Number,
    conscientiousness: Number,
    profileType: String,
    teamData: Array,
    comparingProfiles: Object,
    chartLabels: Object
  }

  connect() {
    // Initialize charts when controller connects
    this.initializeCharts();
  }

  initializeCharts() {
    // Create all charts that are available based on targets
    if (this.hasRadarTarget) {
      this.createRadarChart();
    }
    
    if (this.hasBarsTarget) {
      this.createBarChart();
    }
    
    if (this.hasDistributionTarget && this.hasTeamDataValue) {
      this.createDistributionChart();
    }
    
    if (this.hasCompatibilityTarget && this.hasComparingProfilesValue) {
      this.createCompatibilityChart();
    }
    
    if (this.hasBalanceTarget) {
      this.createBalanceIndicator();
    }
  }

  createRadarChart() {
    const ctx = this.radarTarget.getContext('2d');
    
    // DISC colors
    const colors = {
      dominance: 'rgba(239, 68, 68, 0.7)',     // Red
      influence: 'rgba(234, 179, 8, 0.7)',     // Yellow
      steadiness: 'rgba(34, 197, 94, 0.7)',    // Green
      conscientiousness: 'rgba(59, 130, 246, 0.7)'  // Blue
    };
    
    // Create radar chart
    new Chart(ctx, {
      type: 'radar',
      data: {
        labels: ['Dominance', 'Influence', 'Steadiness', 'Conscientiousness'],
        datasets: [{
          label: 'DISC Profile',
          data: [
            this.dominanceValue, 
            this.influenceValue, 
            this.steadinessValue, 
            this.conscientiousnessValue
          ],
          backgroundColor: 'rgba(79, 70, 229, 0.2)',
          borderColor: 'rgba(79, 70, 229, 1)',
          borderWidth: 2,
          pointBackgroundColor: [
            colors.dominance,
            colors.influence,
            colors.steadiness,
            colors.conscientiousness
          ],
          pointBorderColor: '#fff',
          pointHoverBackgroundColor: '#fff',
          pointHoverBorderColor: 'rgba(79, 70, 229, 1)'
        }]
      },
      options: {
        scales: {
          r: {
            angleLines: {
              display: true
            },
            suggestedMin: 0,
            suggestedMax: 100,
            ticks: {
              stepSize: 20
            }
          }
        },
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            callbacks: {
              label: function(context) {
                return context.raw + '%';
              }
            }
          }
        }
      }
    });
  }

  createBarChart() {
    const ctx = this.barsTarget.getContext('2d');
    
    // Create horizontal bar chart for trait strengths
    new Chart(ctx, {
      type: 'bar',
      data: {
        labels: ['Dominance', 'Influence', 'Steadiness', 'Conscientiousness'],
        datasets: [{
          label: 'Trait Strength',
          data: [
            this.dominanceValue, 
            this.influenceValue, 
            this.steadinessValue, 
            this.conscientiousnessValue
          ],
          backgroundColor: [
            'rgba(239, 68, 68, 0.7)',   // Red for D
            'rgba(234, 179, 8, 0.7)',   // Yellow for I
            'rgba(34, 197, 94, 0.7)',   // Green for S
            'rgba(59, 130, 246, 0.7)'   // Blue for C
          ],
          borderColor: [
            'rgb(239, 68, 68)',
            'rgb(234, 179, 8)',
            'rgb(34, 197, 94)',
            'rgb(59, 130, 246)'
          ],
          borderWidth: 1
        }]
      },
      options: {
        indexAxis: 'y',
        scales: {
          x: {
            beginAtZero: true,
            max: 100,
            ticks: {
              callback: function(value) {
                return value + '%';
              }
            }
          }
        },
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            callbacks: {
              label: function(context) {
                return context.raw + '%';
              }
            }
          }
        }
      }
    });
  }

  createDistributionChart() {
    if (!this.hasTeamDataValue || !this.teamDataValue.length) {
      // No team data available for distribution chart
      return;
    }

    const ctx = this.distributionTarget.getContext('2d');
    
    // Calculate average values for each DISC dimension in the team
    const teamData = this.teamDataValue;
    const teamSize = teamData.length;
    
    // Count profiles with high values in each dimension (>50%)
    const highTraitCounts = {
      dominance: teamData.filter(profile => profile.dominance > 50).length,
      influence: teamData.filter(profile => profile.influence > 50).length,
      steadiness: teamData.filter(profile => profile.steadiness > 50).length,
      conscientiousness: teamData.filter(profile => profile.conscientiousness > 50).length
    };
    
    // Percent of team with high values in each dimension
    const percentages = {
      dominance: (highTraitCounts.dominance / teamSize) * 100,
      influence: (highTraitCounts.influence / teamSize) * 100,
      steadiness: (highTraitCounts.steadiness / teamSize) * 100,
      conscientiousness: (highTraitCounts.conscientiousness / teamSize) * 100
    };
    
    // Create bar chart for team distribution
    new Chart(ctx, {
      type: 'bar',
      data: {
        labels: ['High D', 'High I', 'High S', 'High C'],
        datasets: [{
          label: '% of Team',
          data: [
            percentages.dominance,
            percentages.influence,
            percentages.steadiness,
            percentages.conscientiousness
          ],
          backgroundColor: [
            'rgba(239, 68, 68, 0.7)',
            'rgba(234, 179, 8, 0.7)',
            'rgba(34, 197, 94, 0.7)',
            'rgba(59, 130, 246, 0.7)'
          ],
          borderColor: [
            'rgb(239, 68, 68)',
            'rgb(234, 179, 8)',
            'rgb(34, 197, 94)', 
            'rgb(59, 130, 246)'
          ],
          borderWidth: 1
        }]
      },
      options: {
        scales: {
          y: {
            beginAtZero: true,
            max: 100,
            ticks: {
              callback: function(value) {
                return value + '%';
              }
            }
          }
        },
        plugins: {
          tooltip: {
            callbacks: {
              label: function(context) {
                const count = highTraitCounts[['dominance', 'influence', 'steadiness', 'conscientiousness'][context.dataIndex]];
                return `${context.raw.toFixed(1)}% (${count} of ${teamSize})`;
              }
            }
          }
        }
      }
    });
  }

  createCompatibilityChart() {
    if (!this.hasComparingProfilesValue) {
      // No comparing profiles data available for compatibility chart
      return;
    }

    const ctx = this.compatibilityTarget.getContext('2d');
    const profiles = this.comparingProfilesValue;
    
    if (!profiles.primary || !profiles.comparing) {
      return;
    }
    
    const primary = profiles.primary;
    const comparing = profiles.comparing;
    
    // Calculate differences for each dimension
    const dDiff = Math.abs(primary.dominance - comparing.dominance);
    const iDiff = Math.abs(primary.influence - comparing.influence);
    const sDiff = Math.abs(primary.steadiness - comparing.steadiness);
    const cDiff = Math.abs(primary.conscientiousness - comparing.conscientiousness);
    
    // Generate compatibility scores (inverse of differences)
    const dCompatibility = 100 - dDiff;
    const iCompatibility = 100 - iDiff;
    const sCompatibility = 100 - sDiff;
    const cCompatibility = 100 - cDiff;
    
    // Overall compatibility (average of all dimensions)
    const overallCompatibility = (dCompatibility + iCompatibility + sCompatibility + cCompatibility) / 4;
    
    // Create radar chart comparing both profiles
    new Chart(ctx, {
      type: 'radar',
      data: {
        labels: ['Dominance', 'Influence', 'Steadiness', 'Conscientiousness'],
        datasets: [
          {
            label: profiles.primaryLabel || 'Profile 1',
            data: [primary.dominance, primary.influence, primary.steadiness, primary.conscientiousness],
            backgroundColor: 'rgba(79, 70, 229, 0.2)',
            borderColor: 'rgba(79, 70, 229, 1)',
            borderWidth: 2,
            pointBackgroundColor: 'rgba(79, 70, 229, 1)',
            pointBorderColor: '#fff'
          },
          {
            label: profiles.comparingLabel || 'Profile 2',
            data: [comparing.dominance, comparing.influence, comparing.steadiness, comparing.conscientiousness],
            backgroundColor: 'rgba(245, 158, 11, 0.2)',
            borderColor: 'rgba(245, 158, 11, 1)',
            borderWidth: 2,
            pointBackgroundColor: 'rgba(245, 158, 11, 1)',
            pointBorderColor: '#fff'
          }
        ]
      },
      options: {
        scales: {
          r: {
            angleLines: {
              display: true
            },
            suggestedMin: 0,
            suggestedMax: 100
          }
        },
        plugins: {
          tooltip: {
            callbacks: {
              label: function(context) {
                return context.dataset.label + ': ' + context.raw + '%';
              }
            }
          }
        }
      }
    });
    
    // Add compatibility score to the DOM
    const compatibilityScoreElement = document.createElement('div');
    compatibilityScoreElement.className = 'text-center mt-4';
    compatibilityScoreElement.innerHTML = `
      <div class="text-lg font-semibold">Overall Compatibility</div>
      <div class="text-3xl font-bold text-indigo-600">${overallCompatibility.toFixed(1)}%</div>
      <div class="grid grid-cols-4 gap-2 mt-2">
        <div class="text-center">
          <div class="text-sm font-medium text-red-700">D</div>
          <div class="text-sm">${dCompatibility.toFixed(1)}%</div>
        </div>
        <div class="text-center">
          <div class="text-sm font-medium text-yellow-700">I</div>
          <div class="text-sm">${iCompatibility.toFixed(1)}%</div>
        </div>
        <div class="text-center">
          <div class="text-sm font-medium text-green-700">S</div>
          <div class="text-sm">${sCompatibility.toFixed(1)}%</div>
        </div>
        <div class="text-center">
          <div class="text-sm font-medium text-blue-700">C</div>
          <div class="text-sm">${cCompatibility.toFixed(1)}%</div>
        </div>
      </div>
    `;
    
    this.compatibilityTarget.parentNode.appendChild(compatibilityScoreElement);
  }

  createBalanceIndicator() {
    const ctx = this.balanceTarget.getContext('2d');
    
    // Get values
    const d = this.dominanceValue;
    const i = this.influenceValue;
    const s = this.steadinessValue;
    const c = this.conscientiousnessValue;
    
    // Calculate overall balance
    const sum = d + i + s + c;
    const average = sum / 4;
    const variance = (
      Math.pow(d - average, 2) + 
      Math.pow(i - average, 2) + 
      Math.pow(s - average, 2) + 
      Math.pow(c - average, 2)
    ) / 4;
    
    // Calculate standard deviation (as a measure of imbalance)
    const stdDev = Math.sqrt(variance);
    
    // Convert to a balance score (0-100)
    // Higher balance = lower standard deviation 
    // Max standard deviation is around 50 (if values are [100, 100, 0, 0])
    const imbalanceScore = Math.min(100, (stdDev / 0.5));
    const balanceScore = 100 - imbalanceScore;
    
    // Create doughnut chart for balance indicator
    new Chart(ctx, {
      type: 'doughnut',
      data: {
        labels: ['Balance', 'Imbalance'],
        datasets: [{
          data: [balanceScore, imbalanceScore],
          backgroundColor: [
            'rgba(16, 185, 129, 0.7)',  // Green for balance
            'rgba(239, 68, 68, 0.2)'    // Light red for imbalance
          ],
          borderColor: [
            'rgb(16, 185, 129)',
            'rgb(239, 68, 68)'
          ],
          borderWidth: 1
        }]
      },
      options: {
        cutout: '70%',
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            callbacks: {
              label: function(context) {
                if (context.dataIndex === 0) {
                  return `Balance: ${balanceScore.toFixed(1)}%`;
                } else {
                  return `Imbalance: ${imbalanceScore.toFixed(1)}%`;
                }
              }
            }
          }
        }
      }
    });
    
    // Add balance score in center of doughnut
    const balanceTextElement = document.createElement('div');
    balanceTextElement.className = 'text-center mt-2';
    balanceTextElement.innerHTML = `
      <div class="text-sm text-gray-600">Profile Balance</div>
      <div class="text-2xl font-bold ${balanceScore > 70 ? 'text-green-600' : balanceScore > 40 ? 'text-yellow-600' : 'text-red-600'}">
        ${balanceScore.toFixed(1)}%
      </div>
    `;
    
    this.balanceTarget.parentNode.appendChild(balanceTextElement);
  }
}